import { SITE_URL } from "./site";

export interface WPPost {
  id: number;
  slug: string;
  title: { rendered: string };
  excerpt: { rendered: string };
  content: { rendered: string };
  date: string;
  _embedded?: {
    "wp:featuredmedia"?: [{ source_url: string; alt_text: string }];
    "wp:term"?: [Array<{ name: string }>];
  };
}

const WP_API = `${SITE_URL}/wp-json/wp/v2`;

export async function getPosts(count = 10): Promise<WPPost[]> {
  const res = await fetch(`${WP_API}/posts?_embed&per_page=${count}`);
  if (!res.ok) throw new Error("Falha ao buscar posts");
  return res.json();
}

export async function getPost(slug: string): Promise<WPPost> {
  const res = await fetch(`${WP_API}/posts?slug=${slug}&_embed`);
  if (!res.ok) throw new Error("Falha ao buscar post");
  const posts: WPPost[] = await res.json();
  if (!posts.length) throw new Error("Post não encontrado");
  return posts[0];
}

/**
 * Wrap a route loader's fetch so a build-time failure can't ship silently.
 *
 * The blog routes carry `errorElement: <NotFound />`, which is right on the
 * client — if the WordPress API is unreachable mid-session, the friendly 404 is
 * the best thing to show. At build time it is a trap: a transient API hiccup
 * between `getStaticPaths` enumerating the slugs and this loader fetching one
 * of them makes React Router render NotFound, and the SSG writes that to
 * `dist/<slug>/index.html` as a perfectly well-formed page — real <h1>, real
 * copy, noindex — in place of the post. Nothing downstream can tell it apart
 * from a genuine 404.
 *
 * React Router's data layer catches the loader rejection before it can reach
 * anything that would abort the build, so re-throwing harder doesn't help.
 * Instead: report it, and mark the process as failed. `vite-react-ssg build`
 * carries the non-zero code out, so `build:wp`'s `&&` chain stops before the
 * shell and theme files are assembled — the deploy artifacts are visibly
 * incomplete rather than quietly wrong. The rest of the build still runs, so
 * one run surfaces every failing route instead of only the first.
 *
 * Build-time failures also get retried first. The SSG renders every route
 * concurrently, which means ~25 REST requests land on WordPress at once, and
 * measurably some of them come back not-ok — two unrelated posts failed that
 * way during a single verification build here. Those are worth riding out
 * rather than failing on; a real outage still fails after the last attempt.
 *
 * Both branches compile out of the client bundle: Vite replaces
 * `import.meta.env.SSR` with `false` there, so the client keeps exactly one
 * attempt and its existing errorElement behavior.
 */
const BUILD_FETCH_ATTEMPTS = 3;
const BUILD_FETCH_RETRY_MS = 750;

export async function fetchForRoute<T>(label: string, fetcher: () => Promise<T>): Promise<T> {
  const attempts = import.meta.env.SSR ? BUILD_FETCH_ATTEMPTS : 1;

  for (let attempt = 1; ; attempt++) {
    try {
      return await fetcher();
    } catch (error) {
      if (attempt < attempts) {
        await new Promise((resolve) => setTimeout(resolve, BUILD_FETCH_RETRY_MS * attempt));
        continue;
      }
      if (import.meta.env.SSR) {
        process.exitCode = 1;
        console.error(
          `\n[pre-render] FAILED to fetch data for ${label} after ${attempts} attempts: ${
            error instanceof Error ? error.message : String(error)
          }\n` +
            `[pre-render] That route's HTML will be written as a 404 page. ` +
            `This build is NOT deployable — fix the WordPress API and re-run.\n`
        );
      }
      throw error;
    }
  }
}

export function getPostCategory(post: WPPost): string {
  return post._embedded?.["wp:term"]?.[0]?.[0]?.name ?? "Artigo";
}

export function getFeaturedImage(post: WPPost): string {
  return post._embedded?.["wp:featuredmedia"]?.[0]?.source_url ?? "";
}

export function calculateReadTime(content: string): string {
  const words = content.replace(/<[^>]*>/g, "").split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.ceil(words / 200));
  return `${minutes} min de leitura`;
}

export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "");
}
